import React, { useEffect, useMemo, useState } from 'react';
import { normalizeFeatureValue } from '../../utils/featureNormalization';
import { sortFeatureValues } from '../../utils/sortingUtils';
import { getValueGallery, isSystemFeature } from '../../utils/valuePresentation';

const ImageCarousel = ({
    feature,
    value,
    onChange,
    disabled = false,
    isOptionDisabled,
    className = ''
}) => {
    const schema = feature?.valueSchema || feature?.property?.valueSchema || [];
    const values = sortFeatureValues((feature?.values || []).map(normalizeFeatureValue));
    const currentValue = value === undefined || value === null ? '' : String(value);
    const selected = values.find(item => String(item.value) === currentValue) || values.find(item => !isOptionDisabled?.(String(item.value))) || values[0];
    const gallery = getValueGallery(selected, schema, feature);
    const [index, setIndex] = useState(0);
    const safeIndex = gallery.length ? Math.min(index, gallery.length - 1) : 0;
    const current = gallery[safeIndex];

    const go = (delta) => {
        if (!gallery.length) return;
        setIndex((prev) => (prev + delta + gallery.length) % gallery.length);
    };

    const looksLike360 = useMemo(
        () => gallery.length >= 8 || gallery.some(item => /360/i.test(item.label || '')),
        [gallery]
    );

    useEffect(() => {
        setIndex(0);
    }, [selected?.id, selected?.value, gallery.length]);

    return (
        <div className={`image-carousel ${className}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{
                position: 'relative',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                background: 'var(--bg-primary)',
                minHeight: '220px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}>
                {current?.url ? (
                    <img src={current.url} alt={current.label || selected?.displayText || feature?.displayText} style={{ width: '100%', maxHeight: '360px', objectFit: 'contain' }} />
                ) : (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', padding: '2rem' }}>No images on this value yet</div>
                )}
                {gallery.length > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} style={navButtonStyle('left')} aria-label="Previous image">
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                        <button type="button" onClick={() => go(1)} style={navButtonStyle('right')} aria-label="Next image">
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                {looksLike360 && <span>360°</span>}
                {gallery.length > 1 && <span>{safeIndex + 1} / {gallery.length}</span>}
                {current?.label && <span>{current.label}</span>}
            </div>
            {values.length > 1 && onChange && !isSystemFeature(feature) && (
                <select
                    className="input-field"
                    value={selected?.value || ''}
                    disabled={disabled}
                    onChange={(e) => onChange({ target: { value: e.target.value } })}
                    style={{ margin: 0 }}
                >
                    {values.map(item => (
                        <option key={item.id || item.value} value={item.value} disabled={isOptionDisabled?.(String(item.value))}>
                            {item.displayText || item.value}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

const navButtonStyle = (side) => ({
    position: 'absolute',
    top: '50%',
    [side]: '0.75rem',
    transform: 'translateY(-50%)',
    width: '36px',
    height: '36px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    background: 'rgba(255,255,255,0.9)',
    cursor: 'pointer'
});

export default ImageCarousel;
